import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function InvoiceListPage() {
    const { hasRole } = useAuth();
    const isAdmin = hasRole('admin');
    const [invoices, setInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Search state
    const [searchEmail, setSearchEmail] = useState('');
    const [searchCreatedDate, setSearchCreatedDate] = useState('');
    const [searchStatus, setSearchStatus] = useState('');
    const [searchUser, setSearchUser] = useState('');
    const [searchSoNumber, setSearchSoNumber] = useState('');

    // Fetch invoices
    useEffect(() => {
        fetchInvoices();
    }, []);

    const fetchInvoices = async () => {
        try {
            setLoading(true);
            const response = await axios.get('http://localhost:5000/api/invoices');
            console.log("InvoiceList Page Response 34line ",response)
            setInvoices(response.data);
            setFilteredInvoices(response.data);
        } catch (err) {
            console.error('Error fetching invoices:', err);
            setError('Failed to load invoices');
        } finally {
            setLoading(false);
        }
    };

    // Filter invoices based on search
    useEffect(() => {
        let filtered = [...invoices];

        if (searchEmail) {
            filtered = filtered.filter(invoice =>
                invoice.receiverEmailId?.toLowerCase().includes(searchEmail.toLowerCase())
            );
        }

        if (searchCreatedDate) {
            filtered = filtered.filter(invoice => {
                const invoiceDate = invoice.createdAt ? new Date(invoice.createdAt).toISOString().split('T')[0] : '';
                return invoiceDate === searchCreatedDate;
            });
        }

        if (searchSoNumber) {
            filtered = filtered.filter(invoice => invoice.soNumber && invoice.soNumber.toLowerCase().includes(searchSoNumber.toLowerCase()));
        }

        if (searchStatus) {
            if (searchStatus === 'so-pending') {
                filtered = filtered.filter(invoice => invoice.createdSO && !invoice.soNumber);
            } else {
                filtered = filtered.filter(invoice => invoice.status === searchStatus);
            }
        }

        if (searchUser && isAdmin) {
            filtered = filtered.filter(invoice =>
                invoice.createdBy?.name?.toLowerCase().includes(searchUser.toLowerCase()) ||
                invoice.createdBy?.email?.toLowerCase().includes(searchUser.toLowerCase())
            );
        }

        setFilteredInvoices(filtered);
        setCurrentPage(1); // Reset to first page when filtering
    }, [searchEmail, searchCreatedDate, searchStatus, searchUser, searchSoNumber, invoices, isAdmin]);

    // Pagination calculations
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentInvoices = filteredInvoices.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo(0, 0);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const getPaymentStatus = (invoice) => {
        if (invoice.paymentReceivedDate) {
            return { label: 'Paid', color: '#10b981' }; // Green
        }
        return { label: 'Pending', color: '#fbbf24' }; // Yellow
    };

    const getInvoiceStatus = (invoice) => {


    // Mass Order
    if (invoice.typeOfOrder === 'Mass Order') {
        const allSOsCreated =
            invoice.soSchedule?.length > 0 &&
            invoice.soSchedule.every(so => so.crmSoNumber);

        if (allSOsCreated) {
            return { label: 'Completed', color: '#10b981' };
        }

        return { label: 'SO Pending', color: '#f59e0b' };
    }
        
        // SO creation initiated but SO number not yet assigned
        if (invoice.createdSO && !invoice.soNumber) {
            return { label: 'SO Pending', color: '#f59e0b' }; // Amber
        }
        // SO creation initiated and SO number available
        if (invoice.createdSO && invoice.soNumber) {
            return { label: 'Completed', color: '#10b981' }; // Green
        }

        
        switch (invoice.status) {
            case 'draft':
                return { label: 'Draft', color: '#6b7280' }; // Gray
            case 'pending-review':
                return { label: 'In Review', color: '#8b5cf6' }; // Purple
            case 'completed':
                return { label: 'Completed', color: '#10b981' }; // Green
            default:
                return { label: invoice.status || 'Unknown', color: '#9ca3af' };
        }
    };

    const formatDate = (dateString, fallbackIST) => {
        if (!dateString && !fallbackIST) return 'N/A';
        // If server provided IST display string as fallbackIST, prefer it
        if (fallbackIST) return fallbackIST;
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading Sales Orders...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    return (
        <div>
            <h1 className="page-title">📋 Sales Order List</h1>

            {/* Search Filters */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="form-section">
                    <h2 className="form-section-title">Search Filters</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Search by Email</label>
                            <input
                                type="text"
                                placeholder="Enter email address..."
                                value={searchEmail}
                                onChange={(e) => setSearchEmail(e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Search by Created Date</label>
                            <input
                                type="date"
                                value={searchCreatedDate}
                                onChange={(e) => setSearchCreatedDate(e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Search by SO Number</label>
                            <input
                                type="text"
                                placeholder="Enter SO number..."
                                value={searchSoNumber}
                                onChange={(e) => setSearchSoNumber(e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Filter by Status</label>
                            <select
                                value={searchStatus}
                                onChange={(e) => setSearchStatus(e.target.value)}
                                style={{
                                    padding: '0.75rem',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    width: '100%',
                                    background: 'white'
                                }}
                            >
                                <option value="">All Status</option>
                                <option value="draft">Draft</option>
                                <option value="pending-review">Pending Review</option>
                                <option value="so-pending">SO Pending</option>
                                <option value="completed">Completed</option>
                            </select>
                        </div>
                        {isAdmin && (
                            <div className="form-group">
                                <label>Search by Creator</label>
                                <input
                                    type="text"
                                    value={searchUser}
                                    onChange={(e) => setSearchUser(e.target.value)}
                                    placeholder="Name or Email..."
                                />
                            </div>
                        )}
                        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={() => { setSearchEmail(''); setSearchCreatedDate(''); setSearchStatus(''); setSearchUser(''); setSearchSoNumber(''); }}
                                style={{
                                    background: '#6c757d',
                                    color: 'white',
                                    padding: '0.75rem 1.5rem',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                Clear Filters
                            </button>
                        </div>
                    </div>
                </div>
            </div>


            {/* Invoice Table */}
            <div className="card">
                <div className="table-container">
                    <table className="product-table" style={{ width: '100%' }}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Account Name</th>
                                <th>Email</th>
                                {isAdmin && <th>BD Name</th>}
                                <th>SO Created Date</th>
                                <th>Status</th>
                                <th>SO Number</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentInvoices.length === 0 ? (
                                <tr>
                                    <td colSpan={isAdmin ? 8 : 6} style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                                        No Sales Orders found
                                    </td>
                                </tr>
                            ) : (
                                currentInvoices.map((invoice, index) => {
                                    const statusInfo = getInvoiceStatus(invoice);
                                    const sequentialNumber = indexOfFirstItem + index + 1; // Calculate sequential number
                                    return (
                                        <tr key={invoice.id}>
                                            <td style={{ fontWeight: '600', fontSize: '1rem', color: '#667eea' }}>
                                                #{sequentialNumber}
                                            </td>
                                            <td>{invoice.customerName || 'N/A'}</td>
                                            <td>{invoice.receiverEmailId || 'N/A'}</td>
                                            {isAdmin && <td>{invoice.bdName || 'N/A'}</td>}
                                           {/* <td>{invoice.createdSOAt || invoice.createdSOAtIST ? formatDate(invoice.createdSOAt, invoice.createdSOAtIST) : 'N/A'}</td> */}


                                           <td>{invoice.createdSOAt || invoice.createdSOAtIST || 'N/A'}</td>
                                            <td>
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        padding: '0.25rem 0.75rem',
                                                        borderRadius: '12px',
                                                        background: statusInfo.color,
                                                        color: 'white',
                                                        fontWeight: '600',
                                                        fontSize: '0.75rem',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {statusInfo.label}
                                                </span>
                                            </td>
                                           <td>
    {invoice.typeOfOrder === 'Mass Order'
        ? invoice.soSchedule?.map((so) => (
            <div key={so.soNumber}>
                {so.crmSoNumber || 'Pending'}
            </div>
        ))
        : (invoice.soNumber || 'N/A')
    }
</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                    <button
                                                        onClick={() => window.location.href = `/invoice/edit/${invoice.id}`}
                                                        title="Edit Sales Order"
                                                        style={{
                                                            padding: '0.6rem',
                                                            background: '#10b981',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: 'all 0.2s',
                                                            boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)'
                                                        }}
                                                        onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                                        onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => window.open(`http://localhost:5000/api/invoices/${invoice.id}/pdf`, '_blank')}
                                                        title="Download PDF"
                                                        style={{
                                                            padding: '0.6rem',
                                                            background: '#6366f1',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: 'all 0.2s',
                                                            boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)'
                                                        }}
                                                        onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                                        onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                            <polyline points="7 10 12 15 17 10"></polyline>
                                                            <line x1="12" y1="15" x2="12" y2="3"></line>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Items per page and count */}
            <div className="card" style={{ marginTop: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label style={{ fontWeight: '600' }}>Items per page:</label>
                        <select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            style={{
                                padding: '0.5rem',
                                borderRadius: '6px',
                                border: '1px solid #ddd',
                                cursor: 'pointer'
                            }}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div style={{ fontWeight: '600', color: '#667eea' }}>
                        Showing {filteredInvoices.length > 0 ? indexOfFirstItem + 1 : 0} to {Math.min(indexOfLastItem, filteredInvoices.length)} of {filteredInvoices.length} Sales Orders
                    </div>
                </div>
            </div>

            {/* Pagination */}
            {
                totalPages > 1 && (
                    <div className="card" style={{ marginTop: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: currentPage === 1 ? '#ddd' : '#667eea',
                                    color: currentPage === 1 ? '#999' : 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                Previous
                            </button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                // Show first page, last page, current page, and pages around current
                                if (
                                    page === 1 ||
                                    page === totalPages ||
                                    (page >= currentPage - 2 && page <= currentPage + 2)
                                ) {
                                    return (
                                        <button
                                            key={page}
                                            onClick={() => handlePageChange(page)}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                background: page === currentPage ? '#764ba2' : '#667eea',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontWeight: '600',
                                                minWidth: '40px'
                                            }}
                                        >
                                            {page}
                                        </button>
                                    );
                                } else if (page === currentPage - 3 || page === currentPage + 3) {
                                    return <span key={page} style={{ padding: '0.5rem' }}>...</span>;
                                }
                                return null;
                            })}

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: currentPage === totalPages ? '#ddd' : '#667eea',
                                    color: currentPage === totalPages ? '#999' : 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )
            }
        </div >
    );
}

export default InvoiceListPage;
